import { useEffect, useMemo, useState } from "react";
import { CheckCircle2, XCircle, ShieldCheck, Clock, Ban, Loader2, Car, Phone, Package, ClipboardList, User as UserIcon, DollarSign } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import SupplierReconciliationDrawer from "./SupplierReconciliationDrawer";

/**
 * AdminSupplierApprovals — admin-only section to vet suppliers.
 * - Tabs: Pending / Approved / Rejected / All (counts live).
 * - Cards show photo, name, email, phone, vehicle, listing/order counts.
 * - Approve/Reject buttons prompt for optional note; back-end deactivates
 *   rejected suppliers' listings automatically.
 */
const TABS = [
  { key: "pending", label: "Pending", icon: Clock, tone: "text-amber-300", bg: "bg-amber-500/10", border: "border-amber-500/30" },
  { key: "approved", label: "Approved", icon: ShieldCheck, tone: "text-emerald-300", bg: "bg-emerald-500/10", border: "border-emerald-500/30" },
  { key: "rejected", label: "Rejected", icon: Ban, tone: "text-red-300", bg: "bg-red-500/10", border: "border-red-500/30" },
  { key: "all", label: "All", icon: UserIcon, tone: "text-white", bg: "bg-ink-surface2", border: "border-ink-border" },
];

export default function AdminSupplierApprovals() {
  const [tab, setTab] = useState("pending");
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [reconcileFor, setReconcileFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      // Always load ALL so we can render live counts in tabs; filter client-side.
      const { data } = await api.get("/admin/suppliers");
      setSuppliers(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const counts = useMemo(() => {
    const c = { pending: 0, approved: 0, rejected: 0, all: suppliers.length };
    for (const s of suppliers) {
      const st = s.approval_status || "approved"; // legacy suppliers default to approved
      if (c[st] !== undefined) c[st] += 1;
    }
    return c;
  }, [suppliers]);

  const filtered = useMemo(() => {
    if (tab === "all") return suppliers;
    return suppliers.filter((s) => (s.approval_status || "approved") === tab);
  }, [suppliers, tab]);

  const act = async (supplier, action) => {
    const verb = action === "approve" ? "Approve" : "Reject";
    const note = window.prompt(`${verb} ${supplier.name}? Optional note (visible in audit log):`, "");
    if (note === null) return; // cancelled
    setBusyId(supplier.user_id);
    try {
      await api.post(`/admin/suppliers/${supplier.user_id}/${action}`, { note: note || null });
      toast.success(`${verb}d ${supplier.name}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div data-testid="admin-supplier-approvals">
      <div className="flex items-center gap-2 mb-3">
        <ShieldCheck size={14} className="text-brand-accent" />
        <div className="text-[10px] uppercase tracking-widest font-black text-ink-textDim">// SUPPLIER APPROVALS</div>
        {counts.pending > 0 && (
          <span className="bg-amber-500 text-black text-[10px] font-black uppercase px-2 py-0.5 rounded-full animate-pulse" data-testid="pending-suppliers-badge">
            {counts.pending} PENDING
          </span>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 flex-wrap mb-3" data-testid="approval-tabs">
        {TABS.map((tt) => {
          const active = tab === tt.key;
          const n = counts[tt.key] || 0;
          return (
            <button
              key={tt.key}
              onClick={() => setTab(tt.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-black uppercase tracking-wider inline-flex items-center gap-1.5 border transition ${active ? `${tt.bg} ${tt.tone} ${tt.border}` : "bg-ink-surface border-ink-border text-ink-textDim hover:text-white"}`}
              data-testid={`approval-tab-${tt.key}`}
            >
              <tt.icon size={12} />
              {tt.label}
              <span className={`ms-1 px-1.5 rounded-full text-[10px] ${active ? "bg-black/30" : "bg-ink-surface2"}`}>{n}</span>
            </button>
          );
        })}
      </div>

      {loading && suppliers.length === 0 ? (
        <div className="bg-ink-surface border border-ink-border rounded-2xl p-8 text-center text-xs text-ink-textDim" data-testid="approval-loading">
          <Loader2 size={16} className="inline animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-ink-surface border border-ink-border rounded-2xl p-8 text-center text-xs text-ink-textDim" data-testid="approval-empty">
          No suppliers in this category.
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2" data-testid="approval-list">
          {filtered.map((s) => (
            <SupplierCard
              key={s.user_id} s={s} onAct={act} busy={busyId === s.user_id}
              onReconcile={() => setReconcileFor(s)}
            />
          ))}
        </div>
      )}
      {reconcileFor && (
        <SupplierReconciliationDrawer
          supplier={reconcileFor}
          onClose={() => setReconcileFor(null)}
        />
      )}
    </div>
  );
}

function SupplierCard({ s, onAct, busy, onReconcile }) {
  const status = s.approval_status || "approved";
  const statusMeta = {
    pending: { label: "PENDING", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
    approved: { label: "APPROVED", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
    rejected: { label: "REJECTED", cls: "bg-red-500/15 text-red-300 border-red-500/30" },
  }[status] || { label: status.toUpperCase(), cls: "bg-ink-surface2 text-white border-ink-border" };

  return (
    <div className="bg-ink-surface border border-ink-border rounded-2xl p-4 flex flex-col gap-3" data-testid={`supplier-card-${s.user_id}`}>
      <div className="flex items-center gap-3">
        {s.picture ? (
          <img src={s.picture} alt="" className="w-12 h-12 rounded-full object-cover border border-ink-border" />
        ) : (
          <div className="w-12 h-12 rounded-full bg-ink-surface2 flex items-center justify-center"><UserIcon size={16} /></div>
        )}
        <div className="min-w-0 flex-1">
          <div className="font-black text-sm truncate" data-testid={`supplier-name-${s.user_id}`}>{s.name}</div>
          <div className="text-[11px] text-ink-textDim truncate lowercase">{s.email}</div>
        </div>
        <span className={`shrink-0 text-[10px] font-black px-2 py-0.5 rounded-full border ${statusMeta.cls}`} data-testid={`supplier-status-${s.user_id}`}>
          {statusMeta.label}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2 text-[11px] text-ink-textDim">
        <div className="flex items-center gap-1.5 truncate"><Phone size={11} /> {s.phone || <span className="italic">no phone</span>}</div>
        <div className="flex items-center gap-1.5 truncate"><Car size={11} /> {s.vehicle?.plate ? `${s.vehicle.plate} · ${s.vehicle.model || "?"}` : <span className="italic">no vehicle</span>}</div>
        <div className="flex items-center gap-1.5"><Package size={11} /> {s.listings_count} listings</div>
        <div className="flex items-center gap-1.5"><ClipboardList size={11} /> {s.orders_count} orders</div>
      </div>

      {s.approval_note && (
        <div className="text-[10px] text-ink-textDim bg-ink-surface2 rounded-lg px-2 py-1 border border-ink-border">
          <span className="font-black uppercase me-1">Note:</span>{s.approval_note}
        </div>
      )}

      <div className="flex gap-2 mt-1">
        {status !== "approved" && (
          <button
            onClick={() => onAct(s, "approve")}
            disabled={busy}
            className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full py-2 text-xs font-black inline-flex items-center justify-center gap-1 disabled:opacity-50"
            data-testid={`approve-btn-${s.user_id}`}
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Approve
          </button>
        )}
        {status !== "rejected" && (
          <button
            onClick={() => onAct(s, "reject")}
            disabled={busy}
            className="flex-1 bg-red-500/20 hover:bg-red-500/30 text-red-300 border border-red-500/30 rounded-full py-2 text-xs font-black inline-flex items-center justify-center gap-1 disabled:opacity-50"
            data-testid={`reject-btn-${s.user_id}`}
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <XCircle size={12} />} Reject
          </button>
        )}
        <button
          onClick={onReconcile}
          className="px-3 py-2 rounded-full text-xs font-black inline-flex items-center gap-1 bg-brand-accent/15 text-brand-accent border border-brand-accent/30 hover:bg-brand-accent/25"
          data-testid={`reconcile-btn-${s.user_id}`}
          title="View payout / transfer / refund history"
        >
          <DollarSign size={12} /> Ledger
        </button>
      </div>
    </div>
  );
}
